/**
 * Password Policy Enforcement Utility
 * Implements comprehensive password strength requirements and validation
 */

// Password policy configuration
export const PASSWORD_POLICY = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  minSpecialChars: 1,
  forbiddenPatterns: [
    /password/i,
    /123456/i,
    /qwerty/i,
    /admin/i,
    /user/i,
    /login/i,
    /welcome/i,
    /letmein/i,
    /monkey/i,
    /dragon/i,
    /master/i,
    /hello/i,
    /iloveyou/i,
    /princess/i,
    /rockyou/i,
    /sunshine/i,
    /lovely/i,
    /shadow/i,
    /michael/i,
    /jennifer/i,
    /joshua/i,
    /matthew/i,
    /ashley/i,
    /christopher/i,
    /amanda/i,
    /jessica/i,
    /daniel/i,
    /sarah/i,
    /david/i,
    /james/i,
    /robert/i,
    /john/i,
    /jennifer/i,
    /jason/i,
    /ryan/i,
    /jacob/i,
    /gary/i,
    /nicholas/i,
    /eric/i,
    /jonathan/i,
    /stephen/i,
    /larry/i,
    /justin/i,
    /scott/i,
    /brandon/i,
    /benjamin/i,
    /samuel/i,
    /gregory/i,
    /alexander/i,
    /patrick/i,
    /jack/i,
    /dennis/i,
    /jerry/i,
    /tyler/i,
    /aaron/i,
    /jose/i,
    /henry/i,
    /douglas/i,
    /adam/i,
    /peter/i,
    /nathan/i,
    /zachary/i,
    /kyle/i,
    /walter/i,
    /harold/i,
    /carl/i,
    /jeremy/i,
    /arthur/i,
    /gerald/i,
    /lawrence/i,
    /sean/i,
    /christian/i,
    /ethan/i,
    /austin/i,
    /joe/i,
    /albert/i,
    /jesse/i,
    /willie/i,
    /ralph/i,
    /mason/i,
    /roy/i,
    /eugene/i,
    /wayne/i,
    /louis/i,
    /philip/i,
    /bobby/i,
    /johnny/i,
    /eugene/i,
    /wayne/i,
    /louis/i,
    /philip/i,
    /bobby/i,
    /johnny/i
  ],
  commonPasswords: [
    'password',
    '123456',
    '123456789',
    '12345678',
    '12345',
    '1234567',
    '1234567890',
    'qwerty',
    'abc123',
    'password123',
    'admin',
    'letmein',
    'welcome',
    'monkey',
    '1234',
    'dragon',
    'master',
    'hello',
    'freedom',
    'whatever',
    'qazwsx',
    'trustno1',
    '654321',
    'jordan23',
    'harley',
    'password1',
    'jordan',
    'superman',
    'princess',
    'master',
    'hello',
    'freedom',
    'whatever',
    'qazwsx',
    'trustno1',
    '654321',
    'jordan23',
    'harley',
    'password1',
    'jordan',
    'superman',
    'princess'
  ]
};

/**
 * Validates password against policy requirements
 * @param {string} password - Password to validate
 * @param {string} email - User's email (to check for email-based patterns)
 * @param {string} username - User's username (to check for username-based patterns)
 * @returns {Object} Validation result with isValid, score, and detailed feedback
 */
export function validatePassword(password, email = '', username = '') {
  const result = {
    isValid: true,
    score: 0,
    maxScore: 100,
    requirements: {
      length: { met: false, message: '' },
      uppercase: { met: false, message: '' },
      lowercase: { met: false, message: '' },
      numbers: { met: false, message: '' },
      specialChars: { met: false, message: '' },
      forbiddenPatterns: { met: false, message: '' },
      commonPasswords: { met: false, message: '' },
      emailPattern: { met: false, message: '' },
      usernamePattern: { met: false, message: '' }
    },
    suggestions: []
  };

  // Length validation
  if (password.length < PASSWORD_POLICY.minLength) {
    result.requirements.length.met = false;
    result.requirements.length.message = `Password must be at least ${PASSWORD_POLICY.minLength} characters long`;
    result.isValid = false;
  } else if (password.length > PASSWORD_POLICY.maxLength) {
    result.requirements.length.met = false;
    result.requirements.length.message = `Password must be no more than ${PASSWORD_POLICY.maxLength} characters long`;
    result.isValid = false;
  } else {
    result.requirements.length.met = true;
    result.requirements.length.message = 'Length requirement met';
    result.score += 15;
  }

  // Uppercase validation
  if (PASSWORD_POLICY.requireUppercase) {
    if (!/[A-Z]/.test(password)) {
      result.requirements.uppercase.met = false;
      result.requirements.uppercase.message = 'Password must contain at least one uppercase letter';
      result.isValid = false;
    } else {
      result.requirements.uppercase.met = true;
      result.requirements.uppercase.message = 'Uppercase requirement met';
      result.score += 15;
    }
  }

  // Lowercase validation
  if (PASSWORD_POLICY.requireLowercase) {
    if (!/[a-z]/.test(password)) {
      result.requirements.lowercase.met = false;
      result.requirements.lowercase.message = 'Password must contain at least one lowercase letter';
      result.isValid = false;
    } else {
      result.requirements.lowercase.met = true;
      result.requirements.lowercase.message = 'Lowercase requirement met';
      result.score += 15;
    }
  }

  // Numbers validation
  if (PASSWORD_POLICY.requireNumbers) {
    if (!/\d/.test(password)) {
      result.requirements.numbers.met = false;
      result.requirements.numbers.message = 'Password must contain at least one number';
      result.isValid = false;
    } else {
      result.requirements.numbers.met = true;
      result.requirements.numbers.message = 'Numbers requirement met';
      result.score += 15;
    }
  }

  // Special characters validation
  if (PASSWORD_POLICY.requireSpecialChars) {
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/;
    if (!specialCharRegex.test(password)) {
      result.requirements.specialChars.met = false;
      result.requirements.specialChars.message = 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)';
      result.isValid = false;
    } else {
      result.requirements.specialChars.met = true;
      result.requirements.specialChars.message = 'Special characters requirement met';
      result.score += 15;
    }
  }

  // Forbidden patterns validation
  const hasForbiddenPattern = PASSWORD_POLICY.forbiddenPatterns.some(pattern => 
    pattern.test(password)
  );
  if (hasForbiddenPattern) {
    result.requirements.forbiddenPatterns.met = false;
    result.requirements.forbiddenPatterns.message = 'Password contains forbidden patterns or common words';
    result.isValid = false;
  } else {
    result.requirements.forbiddenPatterns.met = true;
    result.requirements.forbiddenPatterns.message = 'No forbidden patterns detected';
    result.score += 10;
  }

  // Common passwords validation
  const isCommonPassword = PASSWORD_POLICY.commonPasswords.some(common => 
    password.toLowerCase().includes(common.toLowerCase())
  );
  if (isCommonPassword) {
    result.requirements.commonPasswords.met = false;
    result.requirements.commonPasswords.message = 'Password contains common words or phrases';
    result.isValid = false;
  } else {
    result.requirements.commonPasswords.met = true;
    result.requirements.commonPasswords.message = 'No common passwords detected';
    result.score += 10;
  }

  // Email pattern validation
  if (email) {
    const emailLocal = email.split('@')[0];
    if (password.toLowerCase().includes(emailLocal.toLowerCase())) {
      result.requirements.emailPattern.met = false;
      result.requirements.emailPattern.message = 'Password should not contain your email address';
      result.isValid = false;
    } else {
      result.requirements.emailPattern.met = true;
      result.requirements.emailPattern.message = 'No email patterns detected';
      result.score += 5;
    }
  }

  // Username pattern validation
  if (username) {
    if (password.toLowerCase().includes(username.toLowerCase())) {
      result.requirements.usernamePattern.met = false;
      result.requirements.usernamePattern.message = 'Password should not contain your username';
      result.isValid = false;
    } else {
      result.requirements.usernamePattern.met = true;
      result.requirements.usernamePattern.message = 'No username patterns detected';
      result.score += 5;
    }
  }

  // Generate suggestions
  if (!result.isValid) {
    result.suggestions = generatePasswordSuggestions(result.requirements);
  }

  return result;
}

/**
 * Generates helpful suggestions for password improvement
 * @param {Object} requirements - Requirements object from validation result
 * @returns {Array} Array of suggestion strings
 */
function generatePasswordSuggestions(requirements) {
  const suggestions = [];

  if (!requirements.length.met) {
    suggestions.push('Use a longer password with at least 8 characters');
  }

  if (!requirements.uppercase.met) {
    suggestions.push('Add uppercase letters (A-Z)');
  }

  if (!requirements.lowercase.met) {
    suggestions.push('Add lowercase letters (a-z)');
  }

  if (!requirements.numbers.met) {
    suggestions.push('Add numbers (0-9)');
  }

  if (!requirements.specialChars.met) {
    suggestions.push('Add special characters (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  }

  if (!requirements.forbiddenPatterns.met) {
    suggestions.push('Avoid common words, names, or simple patterns');
  }

  if (!requirements.commonPasswords.met) {
    suggestions.push('Avoid common passwords and dictionary words');
  }

  if (!requirements.emailPattern.met) {
    suggestions.push('Don\'t use your email address in your password');
  }

  if (!requirements.usernamePattern.met) {
    suggestions.push('Don\'t use your username in your password');
  }

  // General suggestions
  suggestions.push('Consider using a passphrase with multiple words');
  suggestions.push('Use a unique password that you haven\'t used elsewhere');
  suggestions.push('Consider using a password manager to generate and store passwords');

  return suggestions;
}

/**
 * Calculates password strength level
 * @param {number} score - Password score (0-100)
 * @returns {Object} Strength level with label and color
 */
export function getPasswordStrength(score) {
  if (score < 30) {
    return { level: 'Very Weak', color: '#ff4444', percentage: score };
  } else if (score < 50) {
    return { level: 'Weak', color: '#ff8800', percentage: score };
  } else if (score < 70) {
    return { level: 'Fair', color: '#ffbb00', percentage: score };
  } else if (score < 90) {
    return { level: 'Good', color: '#88cc00', percentage: score };
  } else {
    return { level: 'Strong', color: '#00aa00', percentage: score };
  }
}

/**
 * Generates a secure password suggestion
 * @param {number} length - Desired password length (default: 12)
 * @returns {string} Generated password
 */
export function generateSecurePassword(length = 12) {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  const allChars = uppercase + lowercase + numbers + specialChars;
  let password = '';
  
  // Ensure at least one character from each required category
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += specialChars[Math.floor(Math.random() * specialChars.length)];
  
  // Fill the rest with random characters
  for (let i = 4; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

/**
 * Checks if password policy should be enforced
 * @param {boolean} isNewUser - Whether this is a new user registration
 * @param {boolean} isPasswordChange - Whether this is a password change
 * @returns {boolean} Whether to enforce password policy
 */
export function shouldEnforcePasswordPolicy(isNewUser, isPasswordChange) {
  // Enforce policy for new users and password changes
  return isNewUser || isPasswordChange;
}
